import React, { useEffect, useMemo, useState } from 'react';
import { AlarmClock, Plus, Pencil, Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const RuleEditor = ({ rule, emps, onClose, onSaved }) => {
  const [f, setF] = useState({ name: rule.name || '', employee_ids: rule.employee_ids || [], start_time: rule.start_time || '09:00', days: rule.days || [0, 1, 2, 3, 4, 5], active: rule.active ?? true });
  const [q, setQ] = useState('');
  const depts = useMemo(() => [...new Set(emps.map((e) => e.department).filter(Boolean))].sort(), [emps]);
  const matches = emps.filter((e) => q && !f.employee_ids.includes(e.id) && `${e.name} ${e.employee_code}`.toLowerCase().includes(q.toLowerCase())).slice(0, 8);
  const add = (ids) => setF((p) => ({ ...p, employee_ids: [...new Set([...p.employee_ids, ...ids])] }));
  const save = async () => {
    try {
      if (rule.id) await api.put(`/api/clock/reminder-rules/${rule.id}`, f);
      else await api.post('/api/clock/reminder-rules', f);
      toast({ title: 'Reminder saved' }); onSaved(); onClose();
    } catch (e) { toast({ title: 'Could not save', description: 'Pick staff, a start time and at least one day', variant: 'destructive' }); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="shift-rule-editor">
        <DialogHeader><DialogTitle>{rule.id ? 'Edit reminder' : 'New clock-in reminder'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Group name (optional), e.g. Morning kitchen" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="shift-rule-name" />
          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold text-gray-600">Shift starts</label>
            <Input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} className="w-32" data-testid="shift-rule-time" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map((d, i) => (
              <button key={d} type="button" onClick={() => setF((p) => ({ ...p, days: p.days.includes(i) ? p.days.filter((x) => x !== i) : [...p.days, i].sort() }))}
                className={`rounded-full border px-2.5 py-0.5 text-xs ${f.days.includes(i) ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-500'}`} data-testid={`shift-rule-day-${d}`}>{d}</button>
            ))}
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-gray-600">Staff ({f.employee_ids.length})</p>
            <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
              {f.employee_ids.map((id) => { const e = emps.find((x) => x.id === id); return (
                <span key={id} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] text-indigo-800">{e?.name || id}
                  <button onClick={() => setF((p) => ({ ...p, employee_ids: p.employee_ids.filter((x) => x !== id) }))}><X className="h-3 w-3" /></button></span>); })}
            </div>
            <Input placeholder="Add staff by name or code…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="shift-rule-staff-search" />
            {matches.map((e) => (
              <button key={e.id} type="button" onClick={() => { add([e.id]); setQ(''); }} className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-gray-50" data-testid={`shift-rule-staff-option-${e.employee_code}`}>
                {e.name} <span className="text-gray-400">{e.employee_code} · {e.department}</span>
              </button>
            ))}
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-[11px] text-gray-500">Add a whole department:</span>
              {depts.map((d) => <button key={d} type="button" onClick={() => add(emps.filter((e) => e.department === d).map((e) => e.id))} className="rounded border px-1.5 py-0.5 text-[10px] text-gray-600 hover:bg-gray-50" data-testid={`shift-rule-dept-${d}`}>+ {d}</button>)}
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.employee_ids.length || !f.days.length} data-testid="shift-rule-save">Save reminder</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ShiftReminders = () => {
  const [rules, setRules] = useState([]);
  const [emps, setEmps] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get('/api/clock/reminder-rules').then((r) => setRules(r.data)).catch(() => {});
  useEffect(() => { load(); api.get('/api/employees').then((r) => setEmps(r.data || [])).catch(() => {}); }, []);
  const toggle = async (r, active) => { await api.put(`/api/clock/reminder-rules/${r.id}`, { ...r, active }); load(); };
  const del = async (r) => { if (window.confirm('Delete this reminder?')) { await api.delete(`/api/clock/reminder-rules/${r.id}`); load(); } };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-3" data-testid="shift-reminders-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-semibold text-gray-900"><AlarmClock className="h-4 w-4 text-indigo-600" /> Shift start clock-in reminders</h3>
          <p className="text-xs text-gray-500">Staff get a phone notification with sound 10 and 5 minutes before their shift if they haven't clocked in (skipped when on leave).</p>
        </div>
        <Button size="sm" onClick={() => setEdit({})} data-testid="shift-rule-add"><Plus className="h-4 w-4 mr-1" /> Add reminder</Button>
      </div>
      {rules.length === 0 && <p className="text-sm text-gray-400">No reminders yet — add one for a person or a group.</p>}
      {rules.map((r) => (
        <div key={r.id} className="flex items-center gap-3 rounded-lg border px-3 py-2" data-testid={`shift-rule-${r.id}`}>
          <span className="text-lg font-bold tabular-nums text-indigo-700">{r.start_time}</span>
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-medium">{r.name || (r.staff.length === 1 ? r.staff[0].name : `${r.staff.length} staff`)}</p>
            <p className="truncate text-[11px] text-gray-500">{r.days.map((d) => DAYS[d]).join(', ')} · {r.staff.map((s) => s.name).join(', ')}</p>
          </div>
          <Switch checked={r.active} onCheckedChange={(v) => toggle(r, v)} data-testid={`shift-rule-toggle-${r.id}`} />
          <button onClick={() => setEdit(r)} className="text-gray-400 hover:text-gray-700" data-testid={`shift-rule-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
          <button onClick={() => del(r)} className="text-gray-400 hover:text-rose-600" data-testid={`shift-rule-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      {edit && <RuleEditor rule={edit} emps={emps} onClose={() => setEdit(null)} onSaved={load} />}
    </div>
  );
};
